import { useCart } from '~/context/OrderingSystem/Cart';
import { useAuth } from '~/context/AuthContext';
import { useEffect, useState } from 'react';
import { useMutation } from 'react-query';

import toast from 'react-hot-toast';
import api2 from '~/services/api2';
import { usePinpad } from '..';

export async function printTableSession(tableSessionId) {
  if (!tableSessionId) {
    return;
  }
  try {
    await api2.post('restaurants/printers/printer-queue', {
      table_session_id: tableSessionId,
      close_after_print: false,
    });
  } catch (error) {
    toast.error('Erro ao enviar para impressão.');
  }
}

export async function printOrderBasket(orderBasketId) {
  if (!orderBasketId) {
    return;
  }
  try {
    await api2.post('restaurants/printers/printer-queue', {
      order_basket_id: orderBasketId,
      close_after_print: false,
    });
  } catch (error) {
    toast.error('Erro ao enviar para impressão.');
  }
}

export default function () {
  const pinpad = usePinpad();
  const { user } = useAuth();
  const { cart, addCartToOrder, closeCart } = useCart();
  const [isSelectedPaymentComponent, setIsSelectedPaymentComponent] =
    useState(true);
  const [isOpenBackToOrder, setIsOpenBackToOrder] = useState(false);
  const [cashbackValue, setCashbackValue] = useState(0);
  const [usingCashback, setUsingCashback] = useState(false);

  function handleUpdateUseCashback(state) {
    setUsingCashback(state);
  }

  function handleUpdateCashbackValue(state) {
    setCashbackValue(state);
  }

  const isBalconyPinpad = user.user_use_pinpad && user.use_pinpad;
  const isBalconyProductsBlocked =
    isSelectedPaymentComponent &&
    isBalconyPinpad &&
    pinpad?.balconySession?.session?.status === 'waiting_payment';

  useEffect(() => {
    if (pinpad.sessionRequestStatus === 'success') {
      const orderBasket = pinpad.balconySession?.bills
        ?.flatMap((bill) => bill.order_baskets)
        ?.find((basket) => basket.order_status === 'waiting_payment');
      if (orderBasket) {
        setIsSelectedPaymentComponent(true);
      } else {
        setIsSelectedPaymentComponent(false);
      }
    }
  }, [pinpad.sessionRequestStatus, pinpad.balconySession]);

  const { mutateAsync } = useMutation({
    mutationFn: () =>
      api2.put('restaurants/close-table-bill', {
        key: pinpad.balconySession?.session?.key,
      }),
    onSuccess: async (res) => {
      closeCart();
      toast.success('Balcão fechado com sucesso.');
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (user.print_balcony_default && !user.print_bills_on_web) {
        await printTableSession(res.data.id);
      }
      pinpad.handleGetBalconySession();
    },
    onError() {
      toast.error('O balcão já foi finalizado.');
    },
    onSettled() {
      setIsSelectedPaymentComponent(false);
    },
  });

  async function handleCloseTableBill(fn) {
    if (fn) {
      await fn();
      closeCart();
      pinpad.handleGetBalconySession();
      return;
    }

    mutateAsync();
  }

  async function handleBalconyOrder({
    forTravel,
    getMenu,
    clubInfo,
    clientId,
    commandTableNumber,
    buyerName,
    discount,
    discountObservation,
    discountObs,
    adminPassword,
    resetBalcony,
  }) {
    if (!cart.some((basket) => basket.amount > 0)) {
      toast.error('Adicione produtos ao carrinho');
      return;
    }

    if (cart.length <= 0) {
      toast.error('Adicione produtos ao carrinho para finalizar o pedido');
      return;
    }

    if (clubInfo.phone?.length > 0) {
      const isValidPhone = /^\(\d{2}\) \d{4,5}-\d{4}$/.test(clubInfo.phone);
      if (!isValidPhone) {
        toast.error('O telefone deve estar no formato (XX) XXXXX-XXXX');
        return;
      }
    }

    const tableId = pinpad?.currentTable?.id;

    if (!tableId) {
      toast.error(
        'Balcão não está disponível. Tente novamente mais tarde ou contacte o suporte.'
      );
      return;
    }

    await addCartToOrder(
      pinpad?.currentTable?.id,
      clientId || null,
      'in_person',
      null,
      { name: buyerName },
      null,
      null,
      false, // false para nao imprimir pois impressao acontecera na hora do fechamento para o caso de balcao pinpad
      commandTableNumber,
      getMenu,
      parseFloat(discount?.replace(',', '.')),
      discountObservation
        ? `${discountObs} - ${discountObservation}`
        : discountObs,
      user.user_id,
      clubInfo.phone,
      null,
      adminPassword,
      null,
      null,
      null,
      'balcony-operation',
      forTravel, // deve depender do checkbox de para viagem
      usingCashback,
      cashbackValue
    );

    pinpad.handleGetBalconySession(pinpad?.currentTable?.id);
    setIsSelectedPaymentComponent(true);
    handleUpdateUseCashback(false);
    handleUpdateCashbackValue(0);
    resetBalcony();
  }

  const { mutateAsync: getPayments, data: paymentsData } = useMutation({
    mutationFn: (id) =>
      api2
        .get('restaurants/payments', {
          params: { table_session_id: id },
        })
        .then((res) => res.data),
  });

  useEffect(() => {
    if (pinpad.balconySession?.session?.id) {
      getPayments(pinpad.balconySession?.session?.id);
    }
  }, [pinpad.balconySession?.session?.id]);

  const { mutateAsync: handleRegenerateBalconyBasket } = useMutation({
    mutationFn: (password) =>
      api2.delete(
        `restaurants/session/cancel/${pinpad.balconySession?.session?.id}`,
        {
          order_cancel_password: password,
          cancel_reason: 'Balcão regenerado',
        }
      ),
    onSuccess: async () => {
      toast.success('Pedido cancelado com sucesso.');

      handleUpdateCashbackValue(0);
      handleUpdateUseCashback(false);
      setIsSelectedPaymentComponent(false);
      setIsOpenBackToOrder(false);
      pinpad.handleGetBalconySession(pinpad?.currentTable?.id);
    },
    onError(error) {
      const message = error?.response?.data?.message;

      if (message) {
        toast.error(message);
        return;
      }

      toast.error('Erro ao alterar pedido.');
    },
  });

  function handleSelectedPaymentComponent() {
    setIsOpenBackToOrder(true);
  }

  function handleCancelPaymentCancelation() {
    setIsOpenBackToOrder(false);
  }

  async function handleSubmitRefactoryOrderBasket(password) {
    await handleRegenerateBalconyBasket(password);
  }

  return {
    isSelectedPaymentComponent,
    isBalconyProductsBlocked,
    isOpenBackToOrder,
    handleSubmitRefactoryOrderBasket,
    handleCancelPaymentCancelation,
    handleSelectedPaymentComponent,
    handleUpdateCashbackValue,
    handleUpdateUseCashback,
    handleCloseTableBill,
    handleBalconyOrder,
    getPayments,
    usingCashback,
    cashbackValue,
    paymentsData,
    ...pinpad,
  };
}
